import type {
  CargonizerCustomer,
  Shipment,
  ShipmentCreateInput,
  ShipmentCreateReturnInput,
  ShipmentCreateViaCargonizerInput,
  ShipmentEndpoint,
  ShipmentEvidence,
  ShipmentList,
  ShipmentPatch,
  TrackingRefreshResult,
} from '../domain/shipment';
import { shipmentsApi as liveShipmentsApi } from './live-shipments-api';
import { shipmentsApi as mockShipmentsApi } from './mock-shipments-api';

/**
 * Cargonizer-backed pallet tracking feature (remora SPEC.md §9a/§9b,
 * tidaltech/koi#7882), backed by leatherback's ShipmentsService.
 */
export interface ShipmentsApi {
  listShipments(): Promise<ShipmentList>;
  getShipment(id: string): Promise<Shipment>;
  updateShipment(patch: ShipmentPatch): Promise<Shipment>;
  createShipment(input: ShipmentCreateInput): Promise<Shipment>;
  createShipmentViaCargonizer(input: ShipmentCreateViaCargonizerInput): Promise<Shipment>;
  // Books a customer return; nothing is saved unless Cargonizer accepts it.
  createReturnShipmentViaCargonizer(input: ShipmentCreateReturnInput): Promise<Shipment>;
  // The Tidal locations returns may be shipped to.
  listReturnDestinations(): Promise<ShipmentEndpoint[]>;
  // The Cargonizer label PDF, for shipments koi booked in Cargonizer.
  getShipmentLabel(id: string): Promise<Blob>;
  listCargonizerCustomers(): Promise<CargonizerCustomer[]>;
  // Hands a pending shipment booked through koi to its carrier in
  // Cargonizer. Needs the loaded version; the consignment can't be changed
  // in Cargonizer afterwards.
  transferShipmentToCarrier(id: string, version: number): Promise<Shipment>;
  /** Cancels a booked shipment that was never sent; note is optional. */
  cancelShipment(id: string, version: number, note?: string): Promise<Shipment>;
  // Carrier evidence recorded for a shipment, oldest event first.
  listShipmentEvidence(shipmentId: string): Promise<ShipmentEvidence[]>;
  // Asks leatherback to check Cargonizer (and Bring, when configured) now.
  refreshShipmentTracking(shipmentIds: string[]): Promise<TrackingRefreshResult>;
}

// Single switch point between the deterministic mock (used by default, tests,
// and CI) and the local live API server — see inventory-api-instance.ts for
// the same pattern applied to InventoryApi.
export const shipmentsApi: ShipmentsApi =
  import.meta.env.VITE_API_MODE === 'live' ? liveShipmentsApi : mockShipmentsApi;
